/**
 * Secure Merchant Authentication Context
 * Industry-standard authentication with proper security measures
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import authService from '../services/authService';

const SecureAuthContext = createContext();

export const useSecureAuth = () => {
  const context = useContext(SecureAuthContext);
  if (!context) {
    throw new Error('useSecureAuth must be used within a SecureAuthProvider');
  }
  return context;
};

export const SecureAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Normalize user data
   */
  const normalizeUser = useCallback((raw) => {
    if (!raw) return null;
    return {
      id: raw.id || raw._id,
      _id: raw.id || raw._id,
      username: raw.username,
      email: raw.email,
      firstName: raw.firstName,
      lastName: raw.lastName,
      phone: raw.phone,
      role: raw.role,
      staffRole: raw.staffRole,
      merchantRole: raw.merchantRole, // Team member role (OWNER, MANAGER, CASHIER, etc.)
      merchantCode: raw.merchantCode, // Public CRN (RVS-XXXXXX) for support lookup + display
      storeId: raw.storeId, // Store ID for team members (they're assigned to a store but don't own it)
      ownsStore: raw.ownsStore || false, // Boolean: true if user owns a store
      ownedStoreId: raw.ownedStoreId || null, // Store ID if user is the owner
      createdAt: raw.createdAt,
      updatedAt: raw.updatedAt,
      impersonation: raw.impersonation, // Include impersonation metadata for admin troubleshooting
      onboardingTourCompleted: raw.onboardingTourCompleted || false, // For onboarding tour persistence
      onboardingCompleted: raw.onboardingCompleted || false, // For checking if merchant has completed setup
      merchantKYC: raw.merchantKYC, // KYC status for dashboard nudges
      // Team member permission fields
      permissions: raw.permissions || [], // Permission codes array from backend
      isTeamMember: raw.isTeamMember || (raw.merchantRole && raw.merchantRole !== 'OWNER'),
      storeOwnerId: raw.storeOwnerId || null, // Store owner's ID (for team members)
      roleName: raw.roleName || null, // Display name for the role
    };
  }, []);

  /**
   * Clear authentication state
   */
  const clearAuth = useCallback(() => {
    setUser(null);
    setIsAuthenticated(false);
    setError(null);
  }, []);

  /**
   * Set authentication state
   */
  const setAuth = useCallback((userData) => {
    const normalizedUser = normalizeUser(userData);
    setUser(normalizedUser);
    setIsAuthenticated(true);
    setError(null);
  }, [normalizeUser]);

  /**
   * Check for impersonation token in URL and set cookie
   */
  const handleImpersonationToken = useCallback(async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const impersonateToken = urlParams.get('impersonate_token');

    if (impersonateToken) {
      try {
        console.log('SecureAuth: Impersonation token found in URL');

        // Send token to backend to set cookie
        const response = await fetch('/api/merchant/auth/set-impersonation-cookie', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ token: impersonateToken })
        });

        if (response.ok) {
          console.log('SecureAuth: Impersonation cookie set successfully');

          // Remove token from URL
          urlParams.delete('impersonate_token');
          const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
          window.history.replaceState({}, '', newUrl);

          return true;
        } else {
          console.error('SecureAuth: Failed to set impersonation cookie');
          return false;
        }
      } catch (error) {
        console.error('SecureAuth: Error setting impersonation cookie:', error);
        return false;
      }
    }
    return false;
  }, []);

  /**
   * Initialize authentication on app start
   */
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setLoading(true);
        console.log('SecureAuth: Initializing authentication...');

        // Check for impersonation token first
        await handleImpersonationToken();

        const { isAuthenticated: authStatus, user: userData } = await authService.checkAuth();
        console.log('SecureAuth: Auth check result:', { authStatus, userData });

        if (authStatus && userData) {
          setAuth(userData);
          console.log('SecureAuth: User authenticated:', userData);
        } else {
          clearAuth();
          console.log('SecureAuth: User not authenticated');
        }
      } catch (error) {
        console.error('SecureAuth: Auth initialization error:', error);
        clearAuth();
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, [setAuth, clearAuth, handleImpersonationToken]);

  /**
   * Login function
   */
  const login = useCallback(async (email, password) => {
    try {
      setLoading(true);
      setError(null);

      const result = await authService.login(email, password);
      
      if (result.success) {
        // Get user profile after successful login
        const userData = await authService.getCurrentUser();
        if (userData) {
          setAuth(userData);
        }
        return { success: true, data: result.data };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (error) {
      const errorMessage = error.message || 'Login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [setAuth]);

  /**
   * Register function
   */
  const register = useCallback(async (userData) => {
    try {
      setLoading(true);
      setError(null);

      const result = await authService.register(userData);
      
      if (result.success) {
        // Get user profile after successful registration
        const userProfile = await authService.getCurrentUser();
        if (userProfile) {
          setAuth(userProfile);
        }
        return { success: true, data: result.data };
      } else {
        setError(result.error);
        return { success: false, error: result.error };
      }
    } catch (error) {
      const errorMessage = error.message || 'Registration failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [setAuth]);

  /**
   * Logout function
   */
  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearAuth();
      // Redirect to login page
      window.location.href = '/login';
    }
  }, [clearAuth]);

  /**
   * Refresh user data.
   *
   * Important: `authService.getCurrentUser()` returns the user
   * object directly (already unwrapped from the backend's
   * `{ success, user, config }` envelope by authService). An
   * earlier version of this function tried to extract `.user` AGAIN
   * which silently produced `undefined`, which normalized to `null`
   * — so a successful refresh would clear `user` while leaving
   * `isAuthenticated: true`. That broke the post-OTP flow: the
   * ProtectedRoutes gate then saw `user?.ownsStore = undefined`,
   * decided `hasStore = false`, and bounced already-onboarded
   * merchants to `/onboarding`. A page refresh "fixed" it because
   * the mount path uses `checkAuth()` which destructures correctly.
   */
  const refreshUser = useCallback(async () => {
    try {
      const userData = await authService.getCurrentUser();
      if (userData) {
        setAuth(userData);
        return userData;
      } else {
        clearAuth();
        return null;
      }
    } catch (error) {
      console.error('Refresh user error:', error);
      clearAuth();
      return null;
    }
  }, [setAuth, clearAuth]);

  /**
   * Phase 3: Refresh permissions (after role change)
   * Fetches fresh permissions from JWT and updates user state
   */
  const refreshPermissions = useCallback(async () => {
    try {
      const response = await fetch('/api/merchant/auth/permissions?refresh=true', {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success && user) {
        // Update user with new permissions
        setUser(prevUser => ({
          ...prevUser,
          permissions: data.permissions || [],
          isOwner: data.isOwner,
          roleName: data.roleName
        }));
      }

      return data;
    } catch (error) {
      console.error('Refresh permissions error:', error);
      return null;
    }
  }, [user]);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Get axios instance for API calls
   */
  const getAxios = useCallback(() => {
    return authService.getAxios();
  }, []);

  const value = {
    // State
    user,
    isAuthenticated,
    loading,
    error,

    // Actions
    login,
    register,
    logout,
    refreshUser,
    refreshPermissions, // Phase 3: Refresh permissions after role change
    clearError,
    getAxios,
  };

  return (
    <SecureAuthContext.Provider value={value}>
      {children}
    </SecureAuthContext.Provider>
  );
};
